/**
 * Phase 5 FE — Visit Detail "Open Adverse Events" prompt (AC3).
 *
 * When the visit's subject has any AE in status `ongoing` or
 * `condition_worsening`, this component renders a banner listing
 * those AEs with a "Review & Update Status" action per row. Clicking
 * opens an inline update-status form that appends a history row and
 * refreshes both this prompt and the subject's AE list cache.
 *
 * Hides itself entirely when there's nothing to review — the AC3
 * "prompted to review and update" only fires on non-empty results.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, ArrowUpRight, X } from 'lucide-react';
import { errorText } from '@/lib/permissionError';
import {
  adverseEvents,
  type AdverseEventCurrentStatus,
  type AdverseEventRow,
} from '@/api/client';

const STATUS_LABELS: Record<AdverseEventCurrentStatus, string> = {
  ongoing: 'Ongoing',
  resolved: 'Resolved',
  resolved_with_sequelae: 'Resolved (sequelae)',
  condition_worsening: 'Worsening',
};

export function OpenAdverseEventsPrompt({ visitId }: { visitId: string }) {
  const q = useQuery({
    queryKey: ['visit', visitId, 'open-adverse-events'],
    queryFn: () => adverseEvents.listForVisit(visitId),
    enabled: !!visitId,
  });

  const [reviewing, setReviewing] = useState<AdverseEventRow | null>(null);

  if (q.isLoading) return null;
  const rows = q.data?.rows ?? [];
  if (rows.length === 0) return null;

  return (
    <div className="rounded border border-amber-200 bg-amber-50 p-4">
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-amber-900">
            {rows.length} open adverse event{rows.length === 1 ? '' : 's'} to review
          </h3>
          <p className="mt-0.5 text-xs text-amber-800">
            AC3 requires the coordinator to review and update AE status at each subsequent
            visit. Update each event below or open the AE for full context.
          </p>
        </div>
      </div>

      <ul className="mt-3 space-y-1.5">
        {rows.map((row) => (
          <li
            key={row.ae_id}
            className="flex items-center justify-between rounded border border-amber-100 bg-white/70 px-3 py-2"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <span className="font-mono text-[11px] text-ink-secondary">{row.reference_number}</span>
                <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
                  {STATUS_LABELS[row.current_status]}
                </span>
                {row.is_serious && (
                  <span className="rounded bg-danger-100 px-1.5 py-0.5 text-[10px] font-medium text-danger-700">
                    SAE
                  </span>
                )}
              </div>
              <div className="mt-0.5 truncate text-xs text-ink-primary" title={row.description}>
                {row.description}
              </div>
            </div>
            <div className="ml-3 flex shrink-0 items-center gap-1">
              <button
                type="button"
                onClick={() => setReviewing(row)}
                className="rounded bg-amber-700 px-2.5 py-1 text-[11px] font-medium text-white hover:bg-amber-800"
              >
                Review & update
              </button>
              <a
                href={`/subjects/${row.subject_id}`}
                title="Open subject"
                className="rounded border border-surface-border p-1 text-ink-muted hover:bg-surface-muted/50"
              >
                <ArrowUpRight className="h-3 w-3" />
              </a>
            </div>
          </li>
        ))}
      </ul>

      {reviewing && (
        <UpdateStatusModal
          ae={reviewing}
          visitId={visitId}
          onClose={() => setReviewing(null)}
          onSaved={() => setReviewing(null)}
        />
      )}
    </div>
  );
}

function UpdateStatusModal({
  ae,
  visitId,
  onClose,
  onSaved,
}: {
  ae: AdverseEventRow;
  visitId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const qc = useQueryClient();
  const [toStatus, setToStatus] = useState<AdverseEventCurrentStatus>('resolved');
  const [reason, setReason] = useState('');

  const options = useMemo(
    () =>
      (['ongoing', 'resolved', 'resolved_with_sequelae', 'condition_worsening'] as AdverseEventCurrentStatus[])
        .filter((s) => s !== ae.current_status),
    [ae.current_status],
  );

  const update = useMutation({
    mutationFn: () =>
      adverseEvents.updateStatus(ae.ae_id, {
        to_status: toStatus,
        reason_notes: reason.trim(),
        visit_id: visitId,
      }),
    onSuccess: () => {
      toast.success(`${ae.reference_number} → ${STATUS_LABELS[toStatus]}.`);
      qc.invalidateQueries({ queryKey: ['visit', visitId, 'open-adverse-events'] });
      qc.invalidateQueries({ queryKey: ['ae', 'list', ae.subject_id] });
      qc.invalidateQueries({ queryKey: ['ae', 'detail', ae.ae_id] });
      onSaved();
    },
    onError: (e) => toast.error(errorText(e, 'Failed')),
  });

  return (
    <div
      className="fixed inset-0 z-[95] flex items-center justify-center p-4 !m-0"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div className="relative w-full max-w-sm rounded-lg border border-surface-border bg-surface-card p-4 shadow-xl">
        <div className="mb-3 flex items-start justify-between">
          <div>
            <h4 className="text-sm font-semibold text-ink-primary">
              Update {ae.reference_number}
            </h4>
            <p className="mt-0.5 text-[11px] text-ink-muted">
              Current: <strong>{STATUS_LABELS[ae.current_status]}</strong>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-ink-muted hover:bg-surface-muted/50"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <label className="block text-xs">
          <span className="font-medium text-ink-secondary">New status</span>
          <select
            value={toStatus}
            onChange={(e) => setToStatus(e.target.value as AdverseEventCurrentStatus)}
            className="mt-1 w-full rounded border border-surface-border px-3 py-1.5 text-sm"
          >
            {options.map((s) => (
              <option key={s} value={s}>{STATUS_LABELS[s]}</option>
            ))}
          </select>
        </label>

        <label className="mt-3 block text-xs">
          <span className="font-medium text-ink-secondary">Reason / notes *</span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            maxLength={2000}
            placeholder="What changed at this visit?"
            className="mt-1 w-full rounded border border-surface-border px-3 py-2 text-sm"
          />
        </label>

        <div className="mt-3 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-surface-border px-3 py-1.5 text-xs font-medium hover:bg-surface-muted/50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!reason.trim() || update.isPending}
            onClick={() => update.mutate()}
            className="rounded bg-accent-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-700 disabled:opacity-50"
          >
            {update.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
